import React, { useEffect, useState } from "react";
import moment from "moment";
import { get, groupBy } from "lodash";
import { InputSwitch } from "primereact/inputswitch";
import { Timeline } from "primereact/timeline";
import { Skeleton } from "primereact/skeleton";
import { useNavigate } from "react-router-dom";
import { Avatar } from "primereact/avatar";
import { Divider } from "primereact/divider";
import { Tag } from "primereact/tag";
import { Tab, TabGroup, TabList, TabPanels, TabPanel } from "@tremor/react";
import { useParams } from "react-router";
import useApi from "hooks/useApi";
import { URLS } from "utils/constants";
import FemaleIcon from "assets/icons/FemaleAvatar.png";
import MaleIcon from "assets/icons/MaleAvatar.png";
import NoProfile from "assets/icons/NoProfile.png";
import { useTranslation } from 'react-i18next';
import { t } from "i18next";

const { GET_EVENTS, GET_METRICS_BY_MODULE, GET_ACTION_URL } = URLS;

const GetModuleName = ({ moduleId }) => {
  const api = useApi();
  const { t } = useTranslation();
  const [moduleData, setModuleData] = useState({});
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setLoading(true);
    api
      .get(`${GET_METRICS_BY_MODULE}/${moduleId}`)
      .then((response) => {
        const res = get(response, "data", {});
        setModuleData(res);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        setModuleData({});
      });
  }, []);
  return <>{loading ? t("LOADING") : moduleData.module_name}</>;
};

const GetActionName = ({ actionId }) => {
  const api = useApi();
  const [actionData, setActionData] = useState({});
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setLoading(true);
    api
      .get(`${GET_ACTION_URL}/${actionId}`)
      .then((response) => {
        const res = get(response, "data", {});
        setActionData(res);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        setActionData({});
      });
  }, []);
  return <>{loading ? t("LOADING") : actionData.action_name}</>;
};

const PatientView = () => {
  const { patientId = "" } = useParams();
  const navigate = useNavigate();
  const api = useApi();
  const { t } = useTranslation();
  
const MODULE = t("MODULE");
const EVENT_TIME = t("EVENT_TIME");
const REFERRED_FROM=t("REFERRED_FROM");
const VISIT=t("VISIT");
const GROUP_BY_MODULE=t("GROUP_BY_MODULE");
const GROUP_BY_VISIT=t("GROUP_BY_VISIT");
const CONTACT_NUMBER=t("CONTACT_NUMBER")
const DOCTORS_NAME = t("DOCTORS_NAME");
const ENCOUNTER_NUMBER = t("ENCOUNTER_NUMBER");
const PATIENT_GENDER = t("PERSONNEL_GENDER");
const INVESTIGATION = t("INVESTIGATION");
const PATIENT_VIEW = t("PATIENT_VIEW");
const NO=t("#NO.")
const LOADING=t("LOADING")
  const [patientDetails, setPatientDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModuleGroupBy, setIsModuleGroupBy] = useState(false);
  const [isVisitGroupBy, setIsVisitGroupBy] = useState(false);
  const [activeTabIndex, setActiveTabIndex] = useState(0); // New state variable for active tab index
  const [firstRecord] = patientDetails || [];
  const {
    personnel_name,
    personnel_contact,
    patient_type,
    personnel_gender,
  } = firstRecord || {};
  let profileIcon = NoProfile;
  if (personnel_gender === "male") {
    profileIcon = MaleIcon;
  } else if (personnel_gender === "female") {
    profileIcon = FemaleIcon;
  }

  useEffect(() => {
    getPatientDetailsByMrn();
  }, [patientId]);

  const getPatientDetailsByMrn = () => {
    setLoading(true);
    api
      .get(`${GET_EVENTS}/mrn/${patientId}`)
      .then((response) => {
        const res = get(response, "data.data", []);
        setPatientDetails(res);
        setLoading(false);
      })
      .catch(() => {
        setPatientDetails([]);
        setLoading(false);
      });
  };

  const getPanelDetails = (item) => (
    <div className="flex">
      {!isModuleGroupBy && (
        <React.Fragment>
          <div className="flex flex-column w-[90px]">
            <span className="text-sm text-tremor-content-strong dark:text-dark-tremor-content-strong font-semibold">
              {MODULE}
            </span>
            <span className="text-slate-400 text-xs">
              <GetModuleName moduleId={item.module_id} />
            </span>
          </div>
          <Divider layout="vertical" />
        </React.Fragment>
      )}
      <div className="flex flex-column w-[90px]">
        <span className="text-sm text-tremor-content-strong dark:text-dark-tremor-content-strong font-semibold">
          {EVENT_TIME}
        </span>
        <span className="text-slate-400 text-xs">
          {moment(item.event_time).format("DD-MM-YYYY HH:mm A")}
        </span>
      </div>
      <Divider layout="vertical" />
      <div className="flex flex-column w-[90px]">
        <span className="text-sm text-tremor-content-strong dark:text-dark-tremor-content-strong font-semibold">
          {DOCTORS_NAME}
        </span>
        <span className="text-slate-400 text-xs">{item.dr_name}</span>
      </div>
      <Divider layout="vertical" />
      <div className="flex flex-column w-[90px]">
        <span className="text-sm text-tremor-content-strong dark:text-dark-tremor-content-strong font-semibold">
          {INVESTIGATION}
        </span>
        <span className="text-slate-400 text-xs">{item.investigation}</span>
      </div>
      <Divider layout="vertical" />
      <div className="flex flex-column w-[90px]">
        <span className="text-sm text-tremor-content-strong dark:text-dark-tremor-content-strong font-semibold">
          {ENCOUNTER_NUMBER}
        </span>
        <span className="text-slate-400 text-xs">{item.encounter_number}</span>
      </div>
      <Divider layout="vertical" />
      <div className="flex flex-column w-[90px]">
        <span className="text-sm text-tremor-content-strong dark:text-dark-tremor-content-strong font-semibold">
          {REFERRED_FROM}
        </span>
        <span className="text-slate-400 text-xs">{item.patient_referrals}</span>
      </div>
    </div>
  );

  const skeletonLoading = () => (
    <React.Fragment>
      <div className="flex align-items-center mb-5">
        <div className="flex gap-2">
          <Skeleton shape="circle" size="4rem" className="mr-2"></Skeleton>
          <div className="flex flex-column">
            <Skeleton height="2rem" width="15rem" className="mb-2"></Skeleton>
            <Skeleton width="5rem" className="mb-2"></Skeleton>
          </div>
          <div className="flex flex-column">
            <Skeleton width="10rem" className="mb-2"></Skeleton>
            <Skeleton width="5rem" className="mb-2"></Skeleton>
          </div>
          <div className="flex flex-column">
            <Skeleton width="10rem" className="mb-2"></Skeleton>
            <Skeleton width="5rem" className="mb-2"></Skeleton>
          </div>
        </div>
      </div>
      <div className="mx-auto flex gap-3">
        <Skeleton height="2rem" width="5rem" className="mb-2"></Skeleton>
        <Skeleton height="2rem" width="5rem" className="mb-2"></Skeleton>
        <Skeleton height="2rem" width="5rem" className="mb-2"></Skeleton>
      </div>
    </React.Fragment>
  );

  const getEventsTimeline = (data) => (
    <Timeline
      value={data}
      opposite={(item) => <GetActionName actionId={item.action_id} />}
      content={(item) => getPanelDetails(item)}
    />
  );

  const groupByEncounterNo = (data) =>
    Object.keys(groupBy(data, "encounter_number")).map((visit, index) => {
      const filtered = data.filter(
        (patient) => patient.encounter_number === visit
      );
      return (
        <div className="flex flex-column gap-2" key={index}>
          <span className="text-md text-tremor-content-strong dark:text-dark-tremor-content-strong font-semibold">
            {VISIT} {index + 1} {NO} {visit}
          </span>
          {getEventsTimeline(filtered)}
        </div>
      );
    });

  return (
    <React.Fragment>
      <div className="mx-auto card">
        <i
          className="pi pi-arrow-circle-left cursor-pointer"
          onClick={() => navigate(-1)}
          style={{ fontSize: "2rem" }}
        />
        <div className="flex justify-content-between">
          <p className="text-xl text-tremor-default text-tremor-content dark:text-dark-tremor-content">
            {PATIENT_VIEW}
          </p>
          <div className="flex gap-3">
            <div className="flex gap-2">
              <label className="font-bold block">{GROUP_BY_MODULE}</label>
              <InputSwitch
                checked={isModuleGroupBy}
                onChange={(e) => setIsModuleGroupBy(e.value)}
              />
            </div>
            <div className="flex gap-2">
              <label className="font-bold block">{GROUP_BY_VISIT}</label>
              <InputSwitch
                checked={isVisitGroupBy}
                onChange={(e) => setIsVisitGroupBy(e.value)}
              />
            </div>
          </div>
        </div>
        {loading ? (
          skeletonLoading()
        ) : (
          <React.Fragment>
            <div className="flex align-items-center mb-5">
              <div className="flex gap-2">
                <Avatar image={profileIcon} size="xlarge" shape="circle" />
                <div className="flex flex-column">
                  <span className="text-3xl text-tremor-content-strong dark:text-dark-tremor-content-strong font-semibold">
                    {personnel_name}
                  </span>
                  <div className="flex justify-content-between align-items-center gap-3">
                    <span className="text-slate-400 text-sm font-semibold">
                      {patientId}
                    </span>
                    <Tag value={`${patient_type}-PATIENT`} />
                  </div>
                </div>
              </div>
              <Divider layout="vertical" />
              <div className="flex flex-column">
                <span className="text-md text-tremor-content-strong dark:text-dark-tremor-content-strong font-semibold">
                  {CONTACT_NUMBER}
                </span>
                <span className="text-slate-400 text-sm">
                  {personnel_contact}
                </span>
              </div>
              <Divider layout="vertical" />
              <div className="flex flex-column">
                <span className="uppercase capitalize text-md text-tremor-content-strong dark:text-dark-tremor-content-strong font-semibold">
                  {PATIENT_GENDER}
                </span>
                <span className="uppercase capitalize text-slate-400 text-sm">
                  {personnel_gender}
                </span>
              </div>
            </div>
            {isModuleGroupBy ? (
            <div className="mx-auto flex gap-3 flex-column">
              <TabGroup onChange={(index) => setActiveTabIndex(index)}>
                <TabList variant="solid">
                  {Object.keys(groupBy(patientDetails, "module_id")).map(
                    (module, index) => (
                    <Tab key={index} className={activeTabIndex === index ? 'tab-active' : ''}>
                      <GetModuleName moduleId={module} />
                    </Tab>
                  )
                  )}
                </TabList>
                <TabPanels className="mt-5">
                  {Object.keys(groupBy(patientDetails, "module_id")).map(
                    (module, index) => {
                    const filtered = patientDetails.filter(
                      (patient) =>
                        Number(patient.module_id) === Number(module)
                    );
                    return (
                      <TabPanel key={index}>
                        {isVisitGroupBy
                          ? groupByEncounterNo(filtered)
                          : getEventsTimeline(filtered)}
                      </TabPanel>
                    );
                  }
                  )}
                </TabPanels>
              </TabGroup>
            </div>
          ) : (
            <div className="mx-auto flex gap-3 flex-column">
              {isVisitGroupBy
                ? groupByEncounterNo(patientDetails)
                : getEventsTimeline(patientDetails)}
            </div>
          )}
          </React.Fragment>
        )}
      </div>
    </React.Fragment>
  );
};

export default PatientView;
